import {
  createContext, useContext, useState, useCallback, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

/**
 * ToastProvider — minimal app-wide notification layer for the fire-and-forget
 * background-jobs UX. Submit a job → toast; it finishes → toast with a
 * one-click action. Portals to <body> so a transformed ancestor can't clip it
 * (memory: React 18 modal gotchas).
 *
 * Intentionally tiny: a bottom-right stack, auto-dismiss, optional action
 * buttons. No external toast dep.
 */

const ToastContext = createContext(null);

export function useToast() {
  // Degrade gracefully if a consumer renders outside the provider (e.g. a unit
  // test mounting a modal in isolation) — calls become no-ops.
  return useContext(ToastContext) || { showToast: () => null, dismissToast: () => {} };
}

let _seq = 0;
function nextId() {
  _seq += 1;
  return `toast-${_seq}`;
}

function triggerDownload(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  // Signed export/redaction URLs carry Content-Disposition: attachment, but
  // _blank keeps an inline-playable mp4 from navigating the SPA's tab away.
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// A download nobody clicked for (a job that finished): a hidden frame, not a new
// tab — a pop-up blocker refuses a tab opened without a click, and the person is
// left with nothing. The notice's own Download button still opens a tab.
function quietDownload(url) {
  const f = document.createElement('iframe');
  f.style.display = 'none';
  f.src = url;
  document.body.appendChild(f);
  setTimeout(() => f.remove(), 120000);
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});
  const navigate = useNavigate();

  const dismissToast = useCallback((id) => {
    setToasts(ts => ts.filter(t => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  /**
   * showToast({ message, icon?, tone?, actions?, autoDownload?, downloadUrl?,
   *             autoDismissMs?, id? }) → id
   *   actions: [{ label, href?, onClick?, primary? }]
   *   tone: 'info' | 'success' | 'error'
   */
  const showToast = useCallback((opts) => {
    const id = opts.id || nextId();
    const toast = {
      id,
      message: opts.message || '',
      icon: opts.icon || null,
      actions: opts.actions || [],
      tone: opts.tone || 'info',
    };
    setToasts(ts => [...ts.filter(t => t.id !== id), toast]);

    if (opts.autoDownload && opts.downloadUrl) quietDownload(opts.downloadUrl);

    // Action toasts linger longer so the user can actually click them; bare
    // confirmations fade fast. Pass autoDismissMs: 0 to make a toast sticky.
    const ms = opts.autoDismissMs !== undefined
      ? opts.autoDismissMs
      : (toast.actions.length ? 12000 : 5000);
    if (ms && ms > 0) {
      if (timers.current[id]) clearTimeout(timers.current[id]);
      timers.current[id] = setTimeout(() => dismissToast(id), ms);
    }
    return id;
  }, [dismissToast]);

  const handleAction = useCallback((action, toastId) => {
    if (action.href) {
      if (/^https?:\/\//i.test(action.href)) triggerDownload(action.href);
      else navigate(action.href);
    }
    if (action.onClick) action.onClick();
    dismissToast(toastId);
  }, [navigate, dismissToast]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {createPortal((
        <div className="toast-stack" aria-live="polite">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast-${t.tone}`} role="status">
              {t.icon && <span className="toast-icon" aria-hidden="true">{t.icon}</span>}
              <span className="toast-msg">{t.message}</span>
              <div className="toast-actions">
                {t.actions.map((a, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`toast-action${a.primary ? ' toast-action-primary' : ''}`}
                    onClick={() => handleAction(a, t.id)}
                  >
                    {a.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="toast-close"
                  title="Dismiss"
                  onClick={() => dismissToast(t.id)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      ), document.body)}
    </ToastContext.Provider>
  );
}
